import { Draft, produce } from 'immer';

import removeDashAndSpaceIfPhoneNumber from '@app/libs/formatter/removeDashAndSpaceIfPhoneNumber';
import {
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_SUCCESS,
  CONTRACT_SETUP_ALL_STATE_CLEAR,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
} from '@app/store/actionTypes';
import {
  ContractSetupAction,
  contractSetupSetParticipants,
} from '@app/store/contract-setup/actions';
import SigningMethodType from '@app/store/documents/SigningMethodType';
import MeansType from '@app/store/MeansType';
import Participant from '@app/store/Participant';
import TemplateSigningMethodType from '@app/store/template/SigningMethodType';

export interface ContractSetupParticipantsByIdState {
  [participantId: string]: Participant;
}

const initialState: ContractSetupParticipantsByIdState = {};

function convertSigningMethodTypeToMeansType(
  type: SigningMethodType | TemplateSigningMethodType | null,
  contactInfo: string | null
) {
  let meansType = MeansType.NONE;
  let means = contactInfo;
  if (type === SigningMethodType.EMAIL) {
    meansType = MeansType.EMAIL;
    means = contactInfo;
  } else if (type === SigningMethodType.KAKAO) {
    meansType = MeansType.PHONE;
    means = contactInfo?.substring(3) ?? null;
  } else if (type === SigningMethodType.IN_PERSON) {
    meansType = MeansType.IN_PERSON;
    means = contactInfo;
  }

  return {
    meansType,
    means: means || '',
  };
}

const reducer = produce(
  (
    draft: Draft<ContractSetupParticipantsByIdState>,
    action: ContractSetupAction
  ): Draft<ContractSetupParticipantsByIdState> => {
    switch (action.type) {
      case CONTRACT_SETUP_ALL_STATE_CLEAR:
      case CONTRACT_SETUP_BY_DRAFT_REQUEST:
      case CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_REQUEST:
        return initialState;
      case CONTRACT_SETUP__DOCUMENT_PARTICIPANTS_FETCH_SUCCESS: {
        const { participants } = action.payload;
        participants.forEach((participant) => {
          const { name, participantId, signingMethod, locale, host } = participant;

          const { means, meansType } = convertSigningMethodTypeToMeansType(
            signingMethod.methodType,
            signingMethod.contactInfo
          );

          draft[participantId] = {
            means,
            meansType,
            name,
            participantId,
            locale,
            host,
          };
        });
        return draft;
      }
      case contractSetupSetParticipants.type: {
        const result: ContractSetupParticipantsByIdState = {};
        action.payload.forEach((participant) => {
          const prevParticipant = draft[participant.participantId];
          result[participant.participantId] = {
            ...participant,
            means: removeDashAndSpaceIfPhoneNumber(participant.means) ?? '',
            role: prevParticipant?.role,
            order: prevParticipant?.order,
          };
        });

        return result;
      }

      case CONTRACT_SETUP_BY_DRAFT_SUCCESS: {
        const { participants } = action.payload;

        participants.forEach(({ id, name, role, signingMethod, locale, host }) => {
          let meansType = MeansType.NONE;
          let means = null;
          if (
            signingMethod.type === MeansType.EMAIL ||
            signingMethod.type === MeansType.SECURE_LINK
          ) {
            meansType = signingMethod.type;
            means = signingMethod.value;
          } else if (signingMethod.type === MeansType.PHONE) {
            meansType = MeansType.PHONE;
            means = signingMethod.value.substring(3) ?? null;
          } else if (signingMethod.type === MeansType.IN_PERSON) {
            meansType = MeansType.IN_PERSON;
            means = signingMethod.value;
          }

          draft[id] = {
            means: means ?? '',
            meansType,
            name: name ?? '',
            role,
            locale,
            participantId: id,
            host,
          };
        });

        return draft;
      }
      default:
        return draft;
    }
  },
  initialState
);

export default reducer;
